import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Toggle from '@radix-ui/react-toggle-group'
import { createLink } from '@tanstack/react-router'
import styled, { css } from 'styled-components'

export const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
`

export const StyledToggleRoot = styled(Toggle.Root)`
  all: unset;
  display: inline-flex;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.25rem;
`

/**
 * Shared toggle item used by the account-menu toggles (units, permission
 * gates, keyboard shortcuts). The resting text `color` is intentionally left
 * unset so each toggle can set its own.
 */
export const StyledToggleItem = styled(Toggle.Item)`
  all: unset;
  color: ${({ theme }) => theme.forecast.secondaryText};
  background-color: ${({ theme }) => theme.forecast.background3};
  height: 1.4rem;
  width: 4rem;
  padding: 0 0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-size: 0.675rem;

  &:hover {
    font-weight: 800;
    color: ${({ theme }) => theme.forecast.defaultText} !important;
    background-color: ${({ theme }) => theme.forecast.background4};
    cursor: pointer;
  }

  &:first-child {
    border-top-left-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
  }

  &:last-child {
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  &[data-state='on'] {
    color: ${({ theme }) => theme.forecast.activeText} !important;
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }

  &:focus {
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.border};
    outline-offset: -2px;
  }
`

/**
 * Menu-aware variant of the account-menu segmented toggle. Because Radix
 * dropdown menus disable Tab and only let arrow keys move between menu items,
 * these are built on DropdownMenu.RadioGroup/RadioItem so they participate in
 * the menu's roving focus and are actually reachable by keyboard.
 */
export const ToggleRadioGroup = styled(DropdownMenu.RadioGroup)`
  display: inline-flex;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.25rem;
`

export const ToggleRadioItem = styled(DropdownMenu.RadioItem)`
  all: unset;
  box-sizing: border-box;
  color: ${({ theme }) => theme.forecast.secondaryText};
  background-color: ${({ theme }) => theme.forecast.background3};
  height: 1.4rem;
  width: 4rem;
  padding: 0 0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-size: 0.675rem;
  cursor: pointer;

  &:hover {
    font-weight: 800;
    color: ${({ theme }) => theme.forecast.defaultText} !important;
    background-color: ${({ theme }) => theme.forecast.background4};
  }

  &:first-child {
    border-top-left-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
  }

  &:last-child {
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }

  &[data-state='checked'] {
    color: ${({ theme }) => theme.forecast.activeText} !important;
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }

  &[data-highlighted] {
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.border};
    outline-offset: -2px;
    background-color: ${({ theme }) => theme.forecast.background4} !important;
  }
`

const selectedButtonStyles = css`
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: bold;
  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
  &:active {
    background-color: ${({ theme }) => theme.forecast.activeButton};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

interface StyledLinkProps {
  $isSelected: boolean
  $isSelectedTab?: boolean
}

export const StyledLink = createLink(styled.a<StyledLinkProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.secondaryText};
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  outline: none;
  padding-left: 0.4rem;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
  &:active {
    background-color: ${({ theme }) => theme.forecast.activeGhostButton};
    color: ${({ theme }) => theme.forecast.defaultText};
    font-weight: 600;
  }
  ${({ $isSelected }) => $isSelected && selectedButtonStyles}

  ${({ $isSelectedTab }) =>
    $isSelectedTab &&
    css`
      box-shadow: inset 3px 0 0 ${({ theme }) => theme.forecast.highlight};
    `}

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.activeButton};
    outline-offset: 2px;
  }
`)
